/** Una página publicada con enlace: se lee sin iniciar sesión, y sólo se lee.
 *
 * Es la única pantalla del portal que no pasa por la sesión, así que no usa nada de lo que
 * depende de ella: ni la barra, ni el contexto del portal, ni las llamadas autenticadas.
 */
import { lazy, Suspense, useEffect, useMemo, useState } from 'react'

import Limite from '../componentes/Limite'
import { Cargando } from '../componentes/piezas'
import { useTemaOscuro } from '../componentes/tema'
import { fondoDePortada } from './Iconos'
import type { Bloque } from './tipos'

const Editor = lazy(() => import('./Editor'))

interface Publica {
  title: string
  icon: string | null
  cover: string | null
  content: Bloque[] | null
  full_width: boolean
  small_text: boolean
  updated_at: string
}

export default function PaginaPublica({ token }: { token: string }) {
  const [pagina, setPagina] = useState<Publica | null>(null)
  const [error, setError] = useState('')
  const oscuro = useTemaOscuro()

  useEffect(() => {
    let vivo = true
    fetch(`/api/publico/${encodeURIComponent(token)}`)
      .then(async (r) => {
        if (r.status === 404) throw new Error('Esta página no existe o ya no es pública.')
        if (r.status === 429) throw new Error('Demasiadas visitas seguidas. Prueba dentro de un momento.')
        if (!r.ok) throw new Error('No se ha podido abrir la página.')
        return (await r.json()) as Publica
      })
      .then((p) => vivo && setPagina(p))
      .catch((e: Error) => vivo && setError(e.message))
    return () => { vivo = false }
  }, [token])

  // Los archivos de la página se piden por su ruta autenticada; en público hay otra, atada al
  // enlace. Se reescribe aquí para que las imágenes se vean sin sesión.
  const contenido = useMemo(() => {
    if (!pagina?.content) return null
    const texto = JSON.stringify(pagina.content).replace(
      /\/api\/guilds\/\d+\/portal\/archivos\//g,
      `/api/publico/${encodeURIComponent(token)}/archivos/`,
    )
    return JSON.parse(texto) as Bloque[]
  }, [pagina, token])

  useEffect(() => {
    if (pagina) document.title = pagina.title || 'Página'
  }, [pagina])

  if (error) {
    return (
      <div className="limite" role="alert">
        <strong>{error}</strong>
      </div>
    )
  }
  if (!pagina) return <div className="pagina pagina--estrecha"><Cargando filas={5} /></div>

  const portada = fondoDePortada(pagina.cover)
  return (
    <div className={`publica${pagina.small_text ? ' pagina-marco--pequeno' : ''}`}>
      {portada && <div className="portada" style={{ background: portada }} />}
      <div className={`pagina${pagina.full_width ? ' pagina--completa' : ''}${portada ? ' pagina--con-portada' : ''}`}>
        {pagina.icon && <div className="pagina__icono pagina__icono--fijo">{pagina.icon}</div>}
        <h1 className="pagina__titulo pagina__titulo--fijo">{pagina.title || 'Sin título'}</h1>
        <Limite compacto>
          <Suspense fallback={<Cargando filas={4} />}>
            <Editor inicial={contenido} editable={false} oscuro={oscuro} alCambiar={() => undefined} />
          </Suspense>
        </Limite>
        <footer className="publica__pie">Publicada desde Barcelona PD · solo lectura</footer>
      </div>
    </div>
  )
}
